import React from 'react';
import {
	Alert,
	Flex,
	Popover,
} from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Icon } from '@fortawesome/fontawesome-svg-core';
import { faWrench } from '@fortawesome/pro-solid-svg-icons';
import {
	PortActionTypes,
	Currencies,
	ShortPortActionTypes,
	LaytimeIntervals,
	PortActionTypeLabels,
} from '@shared/utils/constants';
import { PortRotationEntryGeneratorTypes } from '@shared/utils/port-rotation-entry-generator-types';
import { Values } from '@shared/utils/objectEnums';
import { round } from '@shared/utils/math';
import type { Port } from '@api/utils/ports';
import type { ConsumptionSetProps } from '@api/models/consumption-set';
import type { SeaPassageSettings } from '@api/utils/sequelize/calculateVoyageItineraryDetails/helpers/itineraryConstants';
import type { GetVesselDetailsResponse } from '@api/features/vessels/getVesselDetails';
import Button from '@client/components/Button';
import { EditableColumns } from '@client/components/EditableTableRedux/EditableCellTableRedux';
import Select from '@client/components/Select';
import NumericInput from '@client/components/NumericInput';
import { renderConsumptionSetOptions } from '@client/components/ConsumptionSet/renderConsumptionSetOptions';
import { OtherConsumptionMappings } from '@client/screens/fleet/VesselDetailsScreen/components/ConsumptionSets/helpers';
import styles from '../styles/shared.module.css';
import {
	LocalPortRotation,
	LocalPortRotationWithKey,
} from '../context/hooks/usePortRotationHandlers';
import {
	getConfigColumn,
	getDeleteColumn,
	getDistanceColumn,
	getIdleDaysColumn,
	getPortColumn,
	getSeaDaysColumn,
	getSeaMarginColumn,
	getTurnDaysColumn,
	getTypeColumn,
	getWorkingDaysColumn,
	isBallastLeg,
} from './portRotationSharedColumns';

type Props = {
	onDeletePortRotationEntry: (entry: LocalPortRotation) => void;
	onPortRotationRowChange: (
		id: number,
		attributes: any
	) => void;
	onConfigUpdate: (id: number, routeSettings: SeaPassageSettings) => void;
	speedPopoverOpen: number | null;
	setSpeedPopoverOpen: React.Dispatch<React.SetStateAction<number | null>>;
	currency: Values<typeof Currencies>;
	ports: Port[];
	consumptionSets: Array<ConsumptionSetProps>;
	screens: Partial<Record<string, boolean>>;
	vessel: GetVesselDetailsResponse | null | undefined;
	disabled: boolean;
};

const renderDays = (value: any) => round(value);

export const getPortRotationOtherConsumptionColumns = ({
	onDeletePortRotationEntry,
	onConfigUpdate,
	onPortRotationRowChange,
	speedPopoverOpen,
	setSpeedPopoverOpen,
	ports,
	consumptionSets,
	vessel,
	disabled,
}: Props): EditableColumns<LocalPortRotation> => {
	const otherConsumptionRows = vessel?.otherPerformanceEntries
		.sort((a, b) => Number(a.id) - Number(b.id))
		.map((pe, i) => {
			return {
				title: pe.condition,
				editable: false,
				className: styles.cellNoPadding,
				type: 'number',
				render: (row: LocalPortRotationWithKey) => {
					return (
						<NumericInput
							size="small"
							placeholder={pe.condition}
							className={styles.slimInput}
							addonAfter="days"
							value={row[OtherConsumptionMappings[i].daysKey] as unknown as number}
							variant="borderless"
							onChange={(v) => {
								if (row.id) {
									onPortRotationRowChange(row.id,
										{
											[OtherConsumptionMappings[i].daysKey]: Number(v),
											[OtherConsumptionMappings[i].idKey]: pe.id,
										});
								}
							}}
						/>
					);
				},
			};
		});

	return [
		getDeleteColumn({
			onDelete: onDeletePortRotationEntry,
		}),
		getConfigColumn({
			onConfigSave: onConfigUpdate,
		}),
		getTypeColumn({
			disabled,
			actionTypes: PortActionTypes,
			actionTypeLabels: PortActionTypeLabels,
			shortActionTypeLabels: ShortPortActionTypes,
		}),
		getPortColumn({
			ports,
			disabled,
			isGeneratedPortEditable: isBallastLeg,
		}),
		...(otherConsumptionRows ?? []) as any,
		getDistanceColumn({
			renderDistance: (value) => `${value} `,
		}),
		getSeaMarginColumn({ disabled }),
		{
			title: 'Speed',
			// @ts-ignore
			type: 'number',
			inputProps: {
				// @ts-ignore
				placeholder: 'Speed',
			},
			editable: false,
			width: 75,
			className: (row) => (row.nextEntryId == null ? styles.disabled : styles.calculated),
			render: (row: LocalPortRotation) => row != null && (
				(
					<div
						className={styles.speedProfileContainer}
					>
						<span style={{ whiteSpace: 'nowrap' }}>{`${row.speed} kts`}</span>
						<Popover
							title="Choose consumption set"
							trigger="click"
							open={speedPopoverOpen === row?.id}
							onOpenChange={(open) => (
								open ?
									setSpeedPopoverOpen(row.id) :
									setSpeedPopoverOpen(null)
							)}
							content={(
								<Flex
									gap={10}
									vertical
								>
									<Alert
										style={{ width: 300 }}
										message={
											`The chosen consumption profile will be used to
											calculate the speed for the sea passage between this entry and the next.`
										}
										type="info"
										showIcon
									/>
									<Select
										fullWidth
										value={row.consumptionSetId}
										optionFilterProp="title"
										options={renderConsumptionSetOptions(consumptionSets)}
										disabled={disabled}
										onSelect={(v) => {
											onPortRotationRowChange(row.id, { consumptionSetId: Number(v) });
											setSpeedPopoverOpen(null);
										}}
									/>
								</Flex>
							)}
						>
							<Button
								type="link"
								className={styles.speedProfileBtn}
								icon={(<FontAwesomeIcon size="sm" icon={faWrench as Icon} />)}
							/>
						</Popover>
					</div>
				)),
		},
		{
			title: 'Days',
			children: [
				getSeaDaysColumn({
					title: 'Sea',
					renderDays,
				}),
				getWorkingDaysColumn({
					disabled,
					inputProps: {
						className: styles.slimInput,
					},
					renderDays,
				}),
				getIdleDaysColumn({
					disabled,
					inputProps: {
						className: styles.slimInput,
					},
					renderDays,
				}),
				getTurnDaysColumn({
					disabled,
					inputProps: {
						className: styles.slimInput,
					},
					renderDays,
				}),
			],
		},

		{
			title: 'L/D Rate',
			dataIndex: 'loadDischargeRate',
			type: 'number',
			editable: (row) => (row.generatedBy === PortRotationEntryGeneratorTypes.CARGO) && !disabled,
			className: (row) => (
				row.generatedBy !== PortRotationEntryGeneratorTypes.CARGO ?
					styles.disabled :
					undefined
			),
			width: 60,
			inputProps: {
				allowNegative: false,
				className: styles.slimInput,
			},
		},
		{
			title: 'Unit',
			dataIndex: 'laytimeInterval',
			type: 'select',
			editable: !disabled,
			width: 50,
			inputProps: {
				placeholder: 'Unit',
				className: styles.slimInput,
				popupMatchSelectWidth: false,
				options: [{
					label: '/ day',
					value: LaytimeIntervals.DAY,
				},
				{
					label: '/ hour',
					value: LaytimeIntervals.HOUR,
				}],
			},
			renderValue: (v) => `/ ${v.value} `,
		},
	];
};